import {useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {ReactUtils} from "../../../../utils/ReactUtils";
import type {IInputPropsV5} from "../../../../widgets/input/clicktoedit/InputUtils";
import {TextInputV5} from "../TextInputV5";
import {baseDistance, fontSize, fontWeight} from "../../../styles/styles";
import {colorPalette} from "../../../styles/colorPalette";
import {useAppStore} from "../../../../../StateManager";
import {useClickOutside, useCloseOnKeyboardPress} from "../../../utils";
import {KeyboardListener} from "../../../../../utils/interaction/key/KeyboardListener";

export const MultiLineInputV5 = (props: IInputPropsV5) => {
	const {value, onInput, onBlur, onChange, inline, onClick} = props;
	const [isExtended, setIsExtended] = useState<boolean>(false);
	const [rows, setRows] = useState<number>(0);
	const [isActive, setIsActive] = useState<boolean>(false);
	const appState = useAppStore((state) => state.appState);
	const labelRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLDivElement>(null);

	const onClickMore = (e: React.MouseEvent) => {
		e.stopPropagation();
		setIsExtended((e) => !e);
	};

	const onClickElement = () => {
		setIsActive(true);
	};

	const onBlurElement = () => {
		setIsActive(false);
		onBlur?.();
	};

	const setHeight = () => {
		if (isExtended) {
			return Math.min(rows ?? 1, 25);
		} else {
			return Math.min(rows ?? 1, 5);
		}
	};

	const onEnterPress = (e: KeyboardEvent) => {
		if (isActive && !e.shiftKey) {
			setIsActive(false);
		}
	};

	const onEscPress = () => {
		if (isActive) {
			setIsActive(false);
		}
	};

	useClickOutside([inputRef], onBlurElement, "mouseup", undefined, false, isActive);
	useCloseOnKeyboardPress(onEnterPress, KeyboardListener.KEY_ENTER, [], {enabled: isActive});
	useCloseOnKeyboardPress(onEscPress, KeyboardListener.KEY_ESCAPE, [], {enabled: isActive});

	useEffect(() => {
		const labelContent = labelRef.current?.querySelector<HTMLElement>(".content");
		if (isActive || !labelContent) {
			return;
		}

		let frame: number | null = null;
		let disposed = false;
		const measureRows = () => {
			if (disposed || frame !== null) {
				return;
			}
			// Wait until layout has settled; parent renders must not read layout while creating cells.
			frame = window.requestAnimationFrame(() => {
				frame = null;
				if (!disposed) {
					const nextRows = Math.round(labelContent.scrollHeight / 16) || 0; // 16 is the standard lineheight
					setRows((previousRows) => (previousRows === nextRows ? previousRows : nextRows));
				}
			});
		};
		let previousWidth: number | undefined;
		const resizeObserver =
			typeof ResizeObserver === "undefined"
				? null
				: new ResizeObserver((entries) => {
						const width = entries.find((entry) => entry.target === labelContent)?.contentRect.width;
						if (width !== undefined && width !== previousWidth) {
							previousWidth = width;
							measureRows();
						}
					});

		resizeObserver?.observe(labelContent);
		if (!resizeObserver) {
			window.addEventListener("resize", measureRows);
		}
		measureRows();

		return () => {
			disposed = true;
			resizeObserver?.disconnect();
			window.removeEventListener("resize", measureRows);
			if (frame !== null) {
				window.cancelAnimationFrame(frame);
			}
		};
	}, [isActive, value]);

	const final = appState.actions.getURLsInTextInputs(value);
	let isURL = typeof final === "string" ? final.includes("//") : false; // Checking if the value protocol relative URL

	return isActive ? (
		<MultiLineInputStyled
			ref={inputRef}
			className={ReactUtils.cls("vbox multiline", {inline, isActive})}
		>
			<TextInputV5
				value={value}
				onInput={onInput}
				onChange={onChange}
				onClick={onClick}
				isTextArea={true}
				autoFocus={true}
				style={{height: 16 * setHeight() + 14, minHeight: inline ? 36 : 32}}
			/>
			{rows > 5 && <MoreButtonStyled onClick={onClickMore}>{`See ${isExtended ? "Less" : "More"}...`}</MoreButtonStyled>}
		</MultiLineInputStyled>
	) : (
		<MultiLineLabelStyled
			className={ReactUtils.cls("MultiLineLabel", {extended: isExtended, oneLine: rows === 1})}
			onClick={onClickElement}
			ref={labelRef}
		>
			{isURL ? (
				<div className="content">
					<a
						href={final}
						target="_blank"
					>
						{value}
					</a>
				</div>
			) : (
				<div className="content">{final}</div>
			)}
			{rows > 5 && <MoreButtonStyled onClick={onClickMore}>{`Show ${isExtended ? "Less" : "More"}...`}</MoreButtonStyled>}
		</MultiLineLabelStyled>
	);
};

export const MultiLineLabelStyled = styled.div`
	.content {
		white-space: break-spaces;
		word-break: break-word;
		max-height: 82px;
		position: relative;
		overflow: hidden;
		line-height: ${baseDistance.md};
		margin: ${baseDistance.sm};
	}

	&.extended {
		.content {
			max-height: 400px;
			overflow-y: auto;
		}
	}
`;

export const MoreButtonStyled = styled.div`
	margin-left: ${baseDistance.xs};
	margin-top: ${baseDistance.xs};
	font-size: ${fontSize.sm};
	color: ${colorPalette.primary.c500Primary};
	cursor: pointer;
	font-size: ${fontSize.sm};
	line-height: 16px;
	letter-spacing: 0.0025em;
	align-items: center;
	font-weight: ${fontWeight.bold};
`;

export const MultiLineInputStyled = styled.div`
	border-color: ${colorPalette.primary.c500Primary};
	background-color: ${colorPalette.white};
	border: solid 1px ${colorPalette.primary.c500Primary};
	border-radius: ${baseDistance.xs};

	textarea {
		border: none;
		padding: ${baseDistance.sm};
		background-color: ${colorPalette.white};
		border-radius: ${baseDistance.xs};
	}

	${MoreButtonStyled} {
		font-weight: ${fontWeight.normal};
	}
`;
